import { Component, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import moment from "moment";
import { DatePicker, DatePickerModule } from "primeng/datepicker";
import { IDateRange } from "types/interfaces";
import { lighterBorderVariantDatePicker } from "theme/deskbird-prime-theme/db-variations";
import { ExecPipe } from "../../../../shared/src/lib/pipes/exec.pipe";
import { calendarDayToShortDateString } from "shared-utils";

/**
 * Transform function that converts empty arrays to undefined.
 * This prevents PrimeNG DatePicker from crashing when trying to access
 * array elements that don't exist (e.g., when clicking on year view with no dates).
 */
function normalizeDateRange(value: Date[] | undefined | null): Date[] | undefined {
  if (!value || (Array.isArray(value) && value.length === 0)) {
    return undefined;
  }
  return value;
}

@Component({
  selector: "db-date-range-picker",
  templateUrl: "./date-range-picker.component.html",
  imports: [DatePickerModule, FormsModule, ExecPipe],
})
export class DateRangePickerComponent {
  @ViewChild(DatePicker, { read: DatePicker, static: true })
  datePicker!: DatePicker;
  readonly calendarDayToShortDateString = calendarDayToShortDateString;

  /**
   * Date range input. Automatically converts empty arrays to undefined
   * to prevent PrimeNG from crashing when clicking on year view with no dates selected.
   */
  @Input({ transform: normalizeDateRange }) dateRange?: Date[];

  @Input() showClear = false;
  @Input() showButtonBar = false;
  @Input() maxRangeDays?: number | null = null;
  @Input() placeholder: string = "";
  @Input() iconInContainer = false;
  @Input() size: "normal" | "large" = "large";
  @Input() variant: "lighter-border" | undefined = undefined;
  @Input() minDate?: Date | null = null;
  @Input() maxDate?: Date | null = null;
  @Input() appendTo?: string = "body";
  @Input() dataTestId?: string;
  @Output() dateRangeChange = new EventEmitter<Date[]>();
  @Output() onDateRangeChange = new EventEmitter<IDateRange>();
  @Output() onClear = new EventEmitter<void>();

  protected lighterBorderVariantDatePicker = lighterBorderVariantDatePicker;

  dateRangeChangedHandler(dateRange: [string, string]) {
    if (!dateRange || !dateRange[0] || (!dateRange[0] && !dateRange[1])) {
      this.dateRangeChange.emit([]);
      this.onClear.emit();
      return;
    }

    const [startDateString, endDateString] = dateRange;
    const startDate = moment(startDateString);
    const endDate = endDateString ? moment(endDateString) : null;

    // Emit the raw date array for two-way binding
    this.dateRangeChange.emit(endDate ? [startDate.toDate(), endDate.toDate()] : [startDate.toDate()]);

    if (endDate && this.maxRangeDays) {
      const allowedDaysDiff = this.maxRangeDays - 1;
      const rangeMoreThanAllowedDays = endDate.diff(startDate, "days") > allowedDaysDiff;
      const maxAllowedEndDate = rangeMoreThanAllowedDays ? moment(startDate).add(allowedDaysDiff, "days") : endDate;

      const dateRage = {
        startDate: startDate,
        endDate: maxAllowedEndDate,
      };

      this.onDateRangeChange.emit(dateRage);
    } else {
      const dateRange: IDateRange = { startDate, endDate };
      this.onDateRangeChange.emit(dateRange);
    }
  }
}
